import { isReservedSlug } from './reservedSlugs';
import { routing } from './routing';

export type Locale = (typeof routing.locales)[number];

/**
 * `liveLocales` must be a subset of `routing.locales` (routable). It's a Byway overlay — not a next-intl routing concept.
 * A locale can be routable, but not live (deep-linkable while being built), never the reverse.
 * Definition: a liveLocale is a locale considered content complete & ready for users (middleware redirects) and crawlers (hreflang)
 * This is an allowlist, not a `draftLocales` denylist. Adding nl-NL for Dutch launch
 */
const liveLocales = ['en-GB', 'en-US', 'nl-NL'] as const satisfies readonly Locale[];

export const i18n = {
  defaultLocale: routing.defaultLocale,
  liveLocales,
  locales: routing.locales
} as const;

export function isLiveLocale(value: string): boolean {
  return (i18n.liveLocales as readonly string[]).includes(value);
}

export function isValidLocale(value: string): boolean {
  return (i18n.locales as readonly string[]).includes(value);
}

/**
 * Strips a leading locale segment from a pathname (`/en-US/checkout/details` → `/checkout/details`).
 * For route gates and path classifiers fed by next/navigation's `usePathname`, which returns the
 * prefixed path. Bare paths are permanent, not transitional: `localePrefix: 'as-needed'` serves the
 * default locale unprefixed, so mixed bare/prefixed input is the norm even after full migration.
 * Retire this when consumers move to next-intl's `usePathname` (locale-stripped automatically) —
 * end-of-epic cleanup alongside the Link-chokepoint lint rule.
 */
export function stripLocalePrefix(pathname: string): string {
  const [first, ...rest] = pathname.split('/').filter(Boolean);
  if (first !== undefined && isValidLocale(first)) {
    return `/${rest.join('/')}`;
  }
  return pathname;
}

/**
 * Allowlist: Route prefixes migrated to [lang] segment, receive locale-based redirects.
 * Remove when all routes migrated.
 * Checkout migrates step-by-step, so entries are per-step (/checkout/details) — a bare
 * /checkout prefix would claim pay/paid, which still live in (default) and
 * would 404 after the locale rewrite. Collapse to /checkout when the last step migrates.
 * Adding a route here is only half the migration — imperative navigation to it must swap to
 * `~/i18n/navigation`'s router at the same time; grep `TODO - i18n` for the call sites.
 */
export const i18nRoutes = [
  '/',
  '/account',
  '/about',
  '/blueprint-index',
  '/checkout/details',
  '/checkout/passengers',
  '/destination',
  '/inspiration',
  '/journal',
  '/journey',
  '/journey-template',
  '/legal',
  '/map',
  '/partner'
] as const;

/**
 * Definition: Reserved slugs - middleware app routes, Squarespace proxy pages
 * Excluded from root-level [lang]/(marketing)/[slug] catch-all so locale routing never hijacks them.
 */
const isSingleSegmentGenericCandidate = (pathname: string): boolean => {
  const segments = pathname.split('/').filter(Boolean);
  return segments.length === 1 && !segments[0].includes('.') && !isReservedSlug(segments[0]);
};

/**
 * Consumed by the locale-aware `Link` wrapper - an outgoing link to non-migrated route (e.g. `/journey-template/<id>`)
 * is NOT given a locale prefix (which would 404 on non-default locales).
 * Remove when all routes are migrated.
 */
export function isMigratedPathname(pathname: string): boolean {
  return (
    pathname === '/' ||
    i18nRoutes.some((route) => route !== '/' && (pathname === route || pathname.startsWith(`${route}/`))) ||
    isSingleSegmentGenericCandidate(pathname)
  );
}
